import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import ApiService from '../network/API';
import { copyToClipboard } from '../copyToClipboard';
import { LAB_INSERT_EVENT, setLabTerminalOpen } from '../labCommands';

const RESIZE_PREFIX = '\u0001RESIZE:';
const HISTORY_KEY = 'labCommandHistory';
const HISTORY_LIMIT = 300;

export const isTouchDevice = () =>
    typeof window !== 'undefined' &&
    ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || navigator.maxTouchPoints > 0);

const loadHistory = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const saveToHistory = (command) => {
    const trimmed = command.trim();
    if (trimmed.length < 3) return;
    try {
        const next = [trimmed, ...loadHistory().filter((c) => c !== trimmed)].slice(0, HISTORY_LIMIT);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
        // история — удобство, без неё всё работает
    }
};

// Клавиши, которых нет на экранной клавиатуре телефона. Ctrl и Alt — «залипающие»: нажали, затем букву.
const KEYS = [
    { label: 'Esc', seq: '\x1b' },
    { label: 'Tab', seq: '\t' },
    { label: 'Ctrl', mod: 'ctrl' },
    { label: 'Alt', mod: 'alt' },
    { label: '←', arrow: 'D' },
    { label: '↑', arrow: 'A' },
    { label: '↓', arrow: 'B' },
    { label: '→', arrow: 'C' },
    { label: 'Enter', seq: '\r' },
    { label: '^C', seq: '\x03' },
    { label: '^D', seq: '\x04' },
    { label: '/', seq: '/' },
    { label: '|', seq: '|' },
    { label: '~', seq: '~' },
    { label: '-', seq: '-' },
    { label: '.', seq: '.' },
    { label: '>', seq: '>' },
    { label: ':', seq: ':' },
    { label: '_', seq: '_' },
    { label: '"', seq: '"' },
];

const hideScrollbar = { scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } };

// Кнопки-действия (вставка, копия, клавиатура): квадратные, только иконка.
const iconButtonSx = {
    minWidth: 40, width: 40, px: 0, py: 0.6, flexShrink: 0, color: '#e6edf3', borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.08)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
};

const keyButtonSx = (active) => ({
    minWidth: 0,
    px: 1.25,
    py: 0.6,
    flexShrink: 0,
    fontFamily: 'monospace',
    fontSize: 15,
    lineHeight: 1,
    textTransform: 'none',
    color: active ? '#1e1e1e' : '#7ee787',
    backgroundColor: active ? '#7ee787' : 'rgba(126, 231, 135, 0.08)',
    '&:hover': { backgroundColor: active ? '#7ee787' : 'rgba(126, 231, 135, 0.16)' },
});

// Один xterm.js терминал, подключённый по WebSocket к одному контейнеру лабораторной работы (реальный
// псевдотерминал на бэке — работают vim/nano/htop). mode: inline — в странице, full — на весь экран
// (телефон), hidden — скрыт, но соединение и оболочка живут (чтобы переключение вкладок не убивало shell).
const LabTerminal = ({ sessionId, env, mode = 'inline', onClose, header, ad, onActivity }) => {
    const rootRef = useRef(null);
    const containerRef = useRef(null);
    const termRef = useRef(null);
    const wsRef = useRef(null);
    const lineRef = useRef('');
    const modsRef = useRef({ ctrl: false, alt: false });

    const [mods, setMods] = useState({ ctrl: false, alt: false });
    const [suggestions, setSuggestions] = useState([]);
    const [viewport, setViewport] = useState({ top: 0, height: typeof window !== 'undefined' ? window.innerHeight : 600 });

    // Экранная клавиатура открыта (область просмотра сжалась) — рекламу прячем, чтобы не отнимать место у терминала.
    const maxViewportRef = useRef(0);
    maxViewportRef.current = Math.max(maxViewportRef.current, viewport.height);
    const keyboardOpen = viewport.height < maxViewportRef.current * 0.8;

    const touch = isTouchDevice();
    const full = mode === 'full';
    const hidden = mode === 'hidden';
    const showKeys = touch && !hidden;

    // ---------- отправка в контейнер ----------

    const sendRaw = useCallback((data) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
    }, []);

    const updateSuggestions = useCallback(() => {
        const line = lineRef.current;
        if (line === null || line.length < 2) {
            setSuggestions([]);
            return;
        }
        const found = loadHistory().filter((c) => c.startsWith(line) && c !== line).slice(0, 4);
        setSuggestions(found.map((c) => ({ full: c, rest: c.slice(line.length) })));
    }, []);

    // Приблизительно отслеживаем набираемую строку, чтобы подсказывать команды из истории. Стрелки, Tab и
    // прочие управляющие клавиши делают строку «неизвестной» до ближайшего Enter; в vim/nano ничего не пишем.
    const trackLine = useCallback((data) => {
        const term = termRef.current;
        if (!term || term.buffer.active.type === 'alternate') {
            lineRef.current = null;
        } else if (data === '\r') {
            if (lineRef.current) saveToHistory(lineRef.current);
            lineRef.current = '';
        } else if (data === '\x03' || data === '\x15') {
            lineRef.current = '';
        } else if (data === '\x7f') {
            if (lineRef.current !== null) lineRef.current = lineRef.current.slice(0, -1);
        } else if (data.charCodeAt(0) < 32 || data.includes('\x1b') || data.includes('\r') || data.includes('\n')) {
            lineRef.current = null;
        } else if (lineRef.current !== null) {
            lineRef.current += data;
        }
        updateSuggestions();
    }, [updateSuggestions]);

    const setModifiers = (next) => {
        modsRef.current = next;
        setMods(next);
    };

    // Весь ввод (с клавиатуры, из панели клавиш, подсказок) идёт сюда: применяем Ctrl/Alt и шлём в контейнер.
    const handleInput = useCallback((data) => {
        if (onActivity) onActivity();
        let out = data;
        const { ctrl, alt } = modsRef.current;
        if (ctrl && data.length === 1) {
            const code = data.toUpperCase().charCodeAt(0);
            if (code >= 64 && code <= 95) out = String.fromCharCode(code & 31);
            setModifiers({ ctrl: false, alt });
        } else if (alt && data.length === 1) {
            out = '\x1b' + data;
            setModifiers({ ctrl, alt: false });
        }
        sendRaw(out);
        trackLine(out);
    }, [sendRaw, trackLine, onActivity]);

    const handleInputRef = useRef(handleInput);
    handleInputRef.current = handleInput;

    // ---------- терминал и соединение ----------

    useEffect(() => {
        const mobile = isTouchDevice();
        const term = new Terminal({
            cursorBlink: true,
            fontSize: mobile ? 13 : 14,
            scrollback: 3000,
            theme: { background: '#1e1e1e' },
        });
        const fitAddon = new FitAddon();
        term.loadAddon(fitAddon);
        term.open(containerRef.current);
        try {
            fitAddon.fit();
        } catch {
            // контейнер может быть скрыт (display: none) — подгоним, когда он появится
        }
        termRef.current = term;

        // Соединение с контейнером. При обрыве (сеть моргнула, телефон заблокировали, прокси закрыл молчащее соединение)
        // переподключаемся сами: в образах с tmux возвращаемся в ту же оболочку, в старых — открывается новая.
        let closedByUs = false;
        let attempts = 0;
        let retryTimer = null;
        let ws = null;

        const sendSize = () => {
            if (ws && ws.readyState === WebSocket.OPEN && term.cols > 10) ws.send(`${RESIZE_PREFIX}${term.cols},${term.rows}`);
        };

        const connect = () => {
            const cols = term.cols > 10 ? term.cols : 80;
            const rows = term.rows > 5 ? term.rows : 24;
            const socket = new WebSocket(ApiService.getLabTerminalUrl(sessionId, env, cols, rows));
            socket.binaryType = 'arraybuffer';
            ws = socket;
            wsRef.current = socket;

            socket.onopen = () => {
                if (attempts > 0) term.write('\r\n\x1b[32m[соединение восстановлено]\x1b[0m\r\n');
                attempts = 0;
                sendSize();
            };
            socket.onmessage = (event) => {
                const data = event.data instanceof ArrayBuffer
                    ? new Uint8Array(event.data)
                    : new TextEncoder().encode(event.data);
                term.write(data);
            };
            socket.onclose = (event) => {
                if (closedByUs || socket !== ws) return;
                // 1008 — доступ запрещён (окружение удалено, нет прав): повторять бессмысленно.
                if (event.code === 1008 || attempts >= 20) {
                    term.write('\r\n\x1b[31m[соединение закрыто]\x1b[0m\r\n');
                    return;
                }
                attempts += 1;
                if (attempts === 1) term.write('\r\n\x1b[33m[соединение потеряно, переподключаюсь…]\x1b[0m\r\n');
                retryTimer = setTimeout(connect, Math.min(1000 * 2 ** (attempts - 1), 8000));
            };
            socket.onerror = () => {
                // детали придут в onclose, где решаем, переподключаться ли
            };
        };
        connect();

        // Телефон вернулся из сна / появилась сеть: не ждём таймер, подключаемся сразу.
        const reconnectNow = () => {
            if (closedByUs || (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))) return;
            clearTimeout(retryTimer);
            attempts = Math.max(attempts, 1);
            connect();
        };
        const onVisible = () => { if (document.visibilityState === 'visible') reconnectNow(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', reconnectNow);

        const onResize = term.onResize(sendSize);

        const onData = term.onData((data) => handleInputRef.current(data));

        const refit = () => {
            const el = containerRef.current;
            if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) return;
            try {
                fitAddon.fit();
            } catch {
                // игнорируем: размер подгонится при следующем изменении
            }
        };
        window.addEventListener('resize', refit);
        const resizeObserver = new ResizeObserver(refit);
        resizeObserver.observe(containerRef.current);

        // Вставка команды из текста лекции (кнопка «▶ в терминал»): только в видимый терминал.
        const onInsert = (event) => {
            if (!rootRef.current || rootRef.current.getClientRects().length === 0) return;
            if (event.detail.handled) return;
            event.detail.handled = true;
            term.paste(event.detail.text);
            term.focus();
        };
        window.addEventListener(LAB_INSERT_EVENT, onInsert);

        return () => {
            closedByUs = true;
            clearTimeout(retryTimer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', reconnectNow);
            window.removeEventListener(LAB_INSERT_EVENT, onInsert);
            window.removeEventListener('resize', refit);
            resizeObserver.disconnect();
            onData.dispose();
            onResize.dispose();
            if (ws) ws.close();
            term.dispose();
        };
    }, [sessionId, env]);

    // Пока терминал виден, блоки кода в тексте задания показывают кнопку «в терминал».
    useEffect(() => {
        if (hidden) return undefined;
        setLabTerminalOpen(true);
        return () => setLabTerminalOpen(false);
    }, [hidden]);

    // Терминал стал видимым — подгоняем размер и возвращаем фокус (на телефоне это поднимает клавиатуру).
    useEffect(() => {
        if (hidden) return;
        const t = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            if (!touch && termRef.current) termRef.current.focus();
        }, 50);
        return () => clearTimeout(t);
    }, [hidden, full, touch]);

    // На весь экран: подгоняем высоту под экранную клавиатуру (visualViewport) и не даём странице прокручиваться.
    useEffect(() => {
        if (!full) return undefined;
        const vv = window.visualViewport;
        const update = () => setViewport({
            top: vv ? vv.offsetTop : 0,
            height: vv ? vv.height : window.innerHeight,
        });
        update();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        if (vv) {
            vv.addEventListener('resize', update);
            vv.addEventListener('scroll', update);
        }
        return () => {
            document.body.style.overflow = previousOverflow;
            if (vv) {
                vv.removeEventListener('resize', update);
                vv.removeEventListener('scroll', update);
            }
        };
    }, [full]);

    // ---------- действия панели ----------

    const pressKey = (key) => {
        const term = termRef.current;
        if (key.mod) {
            setModifiers({ ...modsRef.current, [key.mod]: !modsRef.current[key.mod] });
        } else if (key.arrow) {
            const application = term && term.modes && term.modes.applicationCursorKeysMode;
            handleInput((application ? '\x1bO' : '\x1b[') + key.arrow);
        } else {
            handleInput(key.seq);
        }
        // Фокус остаётся в терминале, чтобы клавиатура не пряталась после каждого нажатия.
        if (term) term.focus();
    };

    const screenText = () => {
        const term = termRef.current;
        if (!term) return '';
        const selected = term.getSelection();
        if (selected) return selected;
        const buffer = term.buffer.active;
        const lines = [];
        for (let i = 0; i < buffer.length; i++) {
            const line = buffer.getLine(i);
            if (line) lines.push(line.translateToString(true));
        }
        while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
        return lines.slice(-200).join('\n');
    };

    const paste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                termRef.current.paste(text);
                return;
            }
        } catch {
            // Safari/Firefox могут запретить чтение буфера
        }
        // Браузер не дал прочитать буфер: подсказываем штатный способ — долгое нажатие в терминале → «Вставить».
        termRef.current.write('\r\n\x1b[33m[браузер не разрешил читать буфер: нажмите в терминале и удерживайте → «Вставить»]\x1b[0m\r\n');
        termRef.current.focus();
    };

    const toggleKeyboard = () => {
        const term = termRef.current;
        if (!term || !term.textarea) return;
        if (document.activeElement === term.textarea) term.textarea.blur();
        else term.focus();
    };

    // Кнопки панели не должны забирать фокус у терминала — иначе экранная клавиатура пропадает.
    const keepFocus = (e) => {
        if (e.target.tagName !== 'INPUT') e.preventDefault();
    };

    const rootSx = full
        ? {
            position: 'fixed', left: 0, right: 0, top: viewport.top, height: viewport.height, zIndex: 1400,
            backgroundColor: '#1e1e1e', display: 'flex', flexDirection: 'column',
        }
        : { width: '100%', height: '100%', display: hidden ? 'none' : 'flex', flexDirection: 'column' };

    return (
        <Box ref={rootRef} sx={rootSx}>
            {full && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5, borderBottom: '1px solid #333', flexShrink: 0, color: '#ddd' }}>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#ddd' }} aria-label="Свернуть терминал">
                        <CloseIcon />
                    </IconButton>
                    {header}
                </Stack>
            )}

            {full && ad && React.cloneElement(ad, { hidden: keyboardOpen })}

            <Box
                ref={containerRef}
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    width: '100%',
                    backgroundColor: '#1e1e1e',
                    borderRadius: full ? 0 : 1,
                    overflow: 'hidden',
                    p: 1,
                }}
            />

            {showKeys && (
                <Box onMouseDown={keepFocus} onPointerDown={keepFocus} sx={{ flexShrink: 0, backgroundColor: '#161b22', borderTop: '1px solid #333', pb: 'env(safe-area-inset-bottom)' }}>
                    {suggestions.length > 0 && (
                        <Stack direction="row" spacing={0.75} sx={{ px: 1, pt: 0.75, overflowX: 'auto', ...hideScrollbar }}>
                            {suggestions.map((s) => (
                                <Button
                                    key={s.full}
                                    size="small"
                                    onClick={() => handleInput(s.rest)}
                                    sx={{ ...keyButtonSx(false), px: 1.25, maxWidth: 260, justifyContent: 'flex-start', color: '#e6edf3', backgroundColor: 'rgba(255,255,255,0.08)' }}
                                >
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.full}</Box>
                                </Button>
                            ))}
                        </Stack>
                    )}

                    <Stack direction="row" spacing={0.75} sx={{ px: 1, py: 0.75, overflowX: 'auto', ...hideScrollbar }}>
                        <Tooltip title="Вставить из буфера"><Button size="small" onClick={paste} sx={iconButtonSx} aria-label="Вставить"><ContentPasteIcon fontSize="small" /></Button></Tooltip>
                        <Tooltip title="Скопировать выделенное или экран"><Button size="small" onClick={() => copyToClipboard(screenText())} sx={iconButtonSx} aria-label="Копировать"><ContentCopyIcon fontSize="small" /></Button></Tooltip>
                        <Tooltip title="Показать / скрыть клавиатуру"><Button size="small" onClick={toggleKeyboard} sx={iconButtonSx} aria-label="Клавиатура"><KeyboardIcon fontSize="small" /></Button></Tooltip>
                        <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: '#30363d', flexShrink: 0 }} />
                        {KEYS.map((key) => (
                            <Button
                                key={key.label}
                                size="small"
                                onClick={() => pressKey(key)}
                                sx={keyButtonSx(key.mod ? mods[key.mod] : false)}
                            >
                                {key.label}
                            </Button>
                        ))}
                    </Stack>
                </Box>
            )}

        </Box>
    );
};

export default LabTerminal;
